import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import styles from "./TermsPopup.module.scss";
import Button from "./Button";

/**
 * TermsPopup
 *
 * A full‑screen modal used to show Terms & Conditions (or any generic message).
 *
 * Props:
 *  - isOpen   : boolean – controls visibility
 *  - onClose  : function – called when user presses the close button or ESC
 *  - title    : string   – headline text (default "Terms & Conditions")
 *  - message  : string   – body copy (multiline ok)
 */
const TermsPopup = ({
  isOpen,
  onClose,
  title = "Terms & Conditions",
  message,
}) => {
  const btnRef = useRef(null);

  // focus the button whenever dialog opens
  useEffect(() => {
    if (isOpen && btnRef.current) {
      btnRef.current.focus({ preventScroll: true });
    }
  }, [isOpen]);

  // close on ESC
  useEffect(() => {
    function handleKey(e) {
      if ((e.key === "Escape" || e.key === "Enter") && isOpen) onClose?.();
    }
    document.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className={styles.backdrop} role="dialog" aria-modal="true">
      <div className={styles.modalBox}>
        <h2 className={styles.title}>{title}</h2>
        {message && <p className={styles.message}>{message}</p>}
        <Button
          ref={btnRef}
          className={styles.fullWidthBtn}
          onClick={onClose}
          text={"Close"}
        >
          Close
        </Button>
      </div>
    </div>
  );
};

TermsPopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  message: PropTypes.string.isRequired,
};

export default TermsPopup;